import React, { useEffect, useState } from "react";
import SkeletonLoader from "../SkeletonLoader";
import LanguagePopup from "../LanguageSelection";
import "./SplashScreen.scss";

interface SplashScreenProps {
  appConfig: any;
  onStartScanning: () => void;
  onChooseLanguage: () => void;
  languageConfig: any;
  clientConfig: any;
}

const SplashScreen: React.FC<SplashScreenProps> = ({
  appConfig,
  onStartScanning,
  onChooseLanguage,
  languageConfig,
  clientConfig = {},
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [isLanguagePopupOpen, setIsLanguagePopupOpen] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState("en");
  const splashConfig = clientConfig.splashScreenConfig || {};
  // Helper function to get translated text with backward compatibility
  const getTranslatedText = (textObj: any, fallback = "") => {
    // If it's a string (old config format), return as-is
    if (typeof textObj === "string") return textObj;

    // If it's an object (new multilingual format)
    if (typeof textObj === "object" && textObj !== null) {
      return textObj[currentLanguage] || textObj["en"] || fallback;
    }

    // If no value provided, return fallback
    return fallback;
  };

  // Get current language from URL params, default to 'en'
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const langParam = urlParams.get("lang");

    // Only set language if it exists in the languageOptions, otherwise default to 'en'
    if (
      langParam &&
      splashConfig?.languageOptions?.some((opt) => opt.value === langParam)
    ) {
      setCurrentLanguage(langParam);
    } else {
      setCurrentLanguage("en"); // Always default to English
    }
  }, [splashConfig?.languageOptions]);
  const brandLogo = splashConfig?.brandLogo;
  const brandName = splashConfig?.brandName || "";
  const appTitle = getTranslatedText(
    splashConfig?.appTitle,
    "Product Verification App"
  );
  const startButtonText = getTranslatedText(
    splashConfig?.startButtonText,
    "Get Started"
  );
  const languageButtonText = getTranslatedText(
    splashConfig?.languageButtonText,
    "Choose Language"
  );
  const showLanguageButton = splashConfig?.showLanguageButton !== false;
  const languageOptions = splashConfig?.languageOptions || [];
  const primaryColor = clientConfig?.primaryColor || "#C3231E";
  const languageSelectionConfig = clientConfig?.languageSelectionConfig || {};
  const layout = splashConfig?.layout || {};
  const logoConfig = layout.logo || {};
  const logoImage = layout.logoImage || {};
  const appTitleConfig = layout.appTitle || {};
  const buttonsConfig = layout.buttons || {};

  // Logo styling
  const logoStyle = {
    ...logoConfig,
  };

  const appTitleStyle = {
    ...appTitleConfig,
  };

  const buttonContainerStyle = {};

  useEffect(() => {
    // Simulate minimum loading time
    const timer = setTimeout(() => {
      if (imageLoaded) {
        setIsLoading(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [imageLoaded]);

  const handleImageLoad = () => {
    setImageLoaded(true);
    setTimeout(() => {
      setIsLoading(false);
    }, 500);
  };

  const handleImageError = (e) => {
    e.currentTarget.src = "";
    setImageLoaded(true);
    setTimeout(() => {
      setIsLoading(false);
    }, 500);
  };

  const handleLanguageButtonClick = () => {
    setIsLanguagePopupOpen(true);
  };

  const handleLanguagePopupClose = () => {
    setIsLanguagePopupOpen(false);
  };

  const handleLanguageSelect = (selectedLanguage: string) => {
    // Get current URL and add or update lang parameter
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.set("lang", selectedLanguage);

    // Redirect to the URL with language parameter
    window.location.href = currentUrl.toString();
  };

  return (
    <div
      className="splash-screen"
      style={{
        ...(splashConfig?.backgroundStyles || {}),
      }}
      onContextMenu={(e) => e.preventDefault()}
    >
      {isLoading ? (
        // Skeleton loaders
        <>
          <SkeletonLoader
            width="200px"
            height="80px"
            variant="rounded"
            animation="wave"
            style={{
              position: "absolute",
              top: "30px",
              left: "50%",
              transform: "translateX(-50%)",
            }}
          />

          <SkeletonLoader
            width="250px"
            height="24px"
            variant="text"
            animation="pulse"
            style={{
              position: "absolute",
              top: "180px",
              left: "50%",
              transform: "translateX(-50%)",
            }}
          />

          <div
            style={{
              position: "absolute",
              bottom: "100px",
              left: "50%",
              transform: "translateX(-50%)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "10px",
            }}
          >
            <SkeletonLoader
              width="150px"
              height="45px"
              variant="rounded"
              animation="pulse"
            />
            {showLanguageButton && (
              <SkeletonLoader
                width="150px"
                height="45px"
                variant="rounded"
                animation="pulse"
              />
            )}
          </div>
        </>
      ) : (
        // Actual content
        <>
          <div className="logo-container" style={logoConfig}>
            <img
              src={brandLogo}
              alt={`${brandName} Logo`}
              className="brand-logo"
              style={logoImage}
              onLoad={handleImageLoad}
              onError={handleImageError}
            />
          </div>

          <div className="app-title" style={appTitleStyle}>
            {appTitle}
          </div>

          <div className="button-container" style={buttonContainerStyle}>
            <button
              className="start-button"
              style={{
                ...(buttonsConfig.startButton || {}),
              }}
              onClick={onStartScanning}
            >
              {startButtonText}
            </button>

            {showLanguageButton && languageOptions.length > 0 && (
              <button
                className="language-button"
                style={{
                  ...(buttonsConfig.languageButton || {}),
                }}
                onClick={handleLanguageButtonClick}
              >
                {languageButtonText}
              </button>
            )}
          </div>
        </>
      )}

      {/* Language Popup */}
      <LanguagePopup
        isOpen={isLanguagePopupOpen}
        onClose={handleLanguagePopupClose}
        languageOptions={languageOptions}
        onLanguageSelect={handleLanguageSelect}
        primaryColor={primaryColor}
        currentLanguage={currentLanguage}
        languageSelectionConfig={languageSelectionConfig}
      />

      {/* Preload image */}
      {brandLogo && (
        <img
          src={brandLogo}
          alt=""
          style={{ display: "none" }}
          onLoad={handleImageLoad}
          onError={handleImageError}
        />
      )}
    </div>
  );
};

export default SplashScreen;
